import { FAQPage, WithContext } from "schema-dts";

export interface FAQDataItem {
  question: string;
  answer: string;
}

const getFaqData = (
  specialtyLabel: string,
  therapyLabel: string,
): FAQDataItem[] => [
  {
    question: `What types of ${specialtyLabel}s are available in my area?`,
    answer: `Orchid’s ${specialtyLabel} directory includes a variety of licensed ${specialtyLabel}s. To find the right ${specialtyLabel} for your needs, customize our location filters to narrow down your search results.`,
  },
  {
    question: `How do I book a ${therapyLabel} session online?`,
    answer: `Our ${specialtyLabel} directory streamlines the booking process by making it easy for you to schedule an appointment directly from each ${specialtyLabel}'s Orchid profile. To book a session, find the ${specialtyLabel} you want to see, select a date/time from their listed availability, and follow the steps to confirm your booking within seconds.`,
  },
  {
    question: `Can I schedule an immediate ${therapyLabel} session?`,
    answer: `Sometimes it’s possible to schedule same-day appointments, but it’s dependent on each ${specialtyLabel}'s availability. To see a ${specialtyLabel}'s next available time slot, simply search for ${specialtyLabel}s in your area and check the schedules listed on their profiles. `,
  },
  {
    question: `How do I find a ${specialtyLabel} available outside of regular working hours?`,
    answer: `Many ${specialtyLabel}s on Orchid offer sessions after 5 PM or even during early morning hours. Search for ${specialtyLabel}s in your area to check their availability. `,
  },
  {
    question: `How can I find weekend ${specialtyLabel}s?`,
    answer: `All of Orchid’s search results show ${specialtyLabel}s’ availability, including those who are open for appointments on weekends. Select View all availability on a ${specialtyLabel}'s profile to find out if they’re available on any upcoming weekends. `,
  },
  {
    question: `Is online ${therapyLabel} covered by insurance?`,
    answer: `While many insurance providers cover online ${therapyLabel} sessions, you should consult directly with your insurer to get clarity on whether your benefits cover telehealth and any associated costs.`,
  },
  {
    question: `Can I have a ${therapyLabel} session online through a video call?`,
    answer: `Yes, many ${specialtyLabel}s on our platform offer telehealth services. Consult with your ${specialtyLabel} to learn what their policies are for telehealth sessions. `,
  },
  {
    question: `Are there specialized ${specialtyLabel}s for children and adolescents?`,
    answer: `Yes, we have a range of ${specialtyLabel}s who specialize in child and adolescent mental health care. `,
  },
  {
    question: `How can I determine the effectiveness or ratings of a ${specialtyLabel}?`,
    answer: `Some ${specialtyLabel}s have testimonials from past patients, which can give you insight into the ${specialtyLabel}'s clinical effectiveness and therapeutic rapport.`,
  },
  {
    question: `Are there group ${therapyLabel} sessions available?`,
    answer: `Many ${specialtyLabel}s on our platform offer group ${therapyLabel} sessions. <a href="https://orchid.exchange/group-session">Check out Orchid’s support group directory</a> to see which support groups are currently in session or coming up soon.`,
  },
  {
    question: `Can I find ${specialtyLabel}s who are fluent in other languages?`,
    answer: `Orchid empowers you to search for ${specialtyLabel}s based on the languages they’re fluent in, which gives non-English speakers better access to suitable mental health care.`,
  },
  {
    question: `How do I ensure confidentiality during video sessions?`,
    answer: `All telehealth sessions held via Orchid are encrypted and adhere to strict confidentiality and HIPAA compliance. Additionally, all of our ${specialtyLabel}s are verified as licensed clinicians, which means they are obligated as clinicians to maintain confidentiality regardless of how the session takes place. `,
  },
  {
    question: `Are there ${specialtyLabel}s available for emergency situations?`,
    answer: `<p>Although many ${specialtyLabel}s can address acute concerns, for emergencies we strongly recommend seeking immediate medical attention by dialing 911 or contacting a crisis helpline.</p>
    
    <p>If you’re not sure where else to turn right now, here are some useful resources:</p>
    
    <p>Crisis Text Line<br/>
    Text HOME to 741741</p>
    
    <p>Suicide Prevention Lifeline<br/>
    Call 1-800-273-8255</p>
    
    <p>The Trevor Project Suicide Hotline<br/>
    Text START to 678-678</p>
    
    <p>RAINN Sexual Assault Hotline<br/>
    Call 1-800-656-4674</p>
    
    <p>National Domestic Violence Hotline<br/>
    Text START to 88788</p>
    
    <p>SAMHSA’s Substance Abuse Helpline<br/>
    Call 1-800-662-4357</p>
    `,
  },
];

export const getFaqStructuredData = (
  specialtyLabel: string,
  therapyLabel: string,
): WithContext<FAQPage> => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: getFaqData(specialtyLabel, therapyLabel).map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export default getFaqData;
